import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Map, Languages, Camera, ShieldCheck, Users, ArrowRight, Star,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1685432731505-d3309cf7769f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FEATURES = [
  { icon: Sparkles, title: "AI Itinerary Planner", desc: "Personalized day-by-day plans built around your interests, budget and dates.", to: "/planner", color: "bg-primary" },
  { icon: Languages, title: "Live Darija Translator", desc: "Speak like a local with instant Moroccan Arabic translations and pronunciation.", to: "/explore", color: "bg-secondary" },
  { icon: Camera, title: "Instagram Content Planner", desc: "The best photo spots with golden-hour timing and caption ideas.", to: "/content", color: "bg-accent" },
  { icon: ShieldCheck, title: "Fair Price Guidance", desc: "Never overpay again — know the real local price before you bargain.", to: "/prices", color: "bg-primary" },
  { icon: Users, title: "Trusted Local Services", desc: "Book vetted photographers, drivers and guides across Morocco.", to: "/services", color: "bg-secondary" },
  { icon: Map, title: "Interactive Map", desc: "Discover experiences and hidden gems plotted across every city.", to: "/explore", color: "bg-accent" },
];

export default function Landing() {
  const { user } = useAuth();
  const startLink = user ? "/planner" : "/login";

  return (
    <div className="relative overflow-hidden">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Morocco" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10" />
        </div>
        <div className="relative mx-auto flex min-h-[86vh] max-w-7xl flex-col justify-center px-6 py-24 sm:px-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="overline text-accent"
          >
            Authentic • Personalized • Local
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            Discover the real Morocco, one local experience at a time.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-white/85"
          >
            One AI-powered companion for tailor-made itineraries, a live Darija translator, the best photo
            spots and trusted local guides — no more juggling ten apps.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Button asChild size="lg" className="rounded-full px-7 text-base" data-testid="hero-start-btn">
              <Link to={startLink}>Plan my trip <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-white/10 px-7 text-base text-white hover:bg-white/20 hover:text-white" data-testid="hero-explore-btn">
              <Link to="/explore">Explore the map</Link>
            </Button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex items-center gap-2 text-white/80"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-accent text-accent" />)}
            </div>
            <span className="text-sm">Loved by travelers exploring Marrakech, Fes, Chefchaouen & the Sahara</span>
          </motion.div>
        </div>
      </section>

      {/* Features bento */}
      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10">
        <div className="max-w-2xl">
          <p className="overline text-primary">Everything in one place</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Your complete Moroccan travel companion
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <Link
                to={f.to}
                data-testid={`feature-${i}`}
                className="group flex h-full flex-col rounded-2xl border bg-card p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${f.color} text-white`}>
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold">{f.title}</h3>
                <p className="mt-2 flex-1 leading-relaxed text-muted-foreground">{f.desc}</p>
                <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                  Open <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-24 sm:px-10">
        <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background sm:px-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight text-background sm:text-4xl">
            Ready to travel smarter and more authentically?
          </h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-background/70">
            Create your free account and let Morocco Local craft the trip of a lifetime.
          </p>
          <Button asChild size="lg" className="mt-8 rounded-full px-8" data-testid="cta-btn">
            <Link to={startLink}>Get started free</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
